import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import { AvatarDropdown, AvatarName, Footer, SelectLang } from '@/components';
import { AuthService } from '@/services/auth';
import '@ant-design/v5-patch-for-react-19';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

const isDev = window.location.hostname === 'localhost';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: import('@/types').CurrentUser;
  isAuthenticated?: boolean;
  loading?: boolean;
  fetchUserInfo?: () => Promise<import('@/types').CurrentUser | undefined>;
}> {
  // 执行认证流程
  const authResult = await AuthService.authenticate();

  // 如果需要跳转到登录页面，直接跳转
  if (authResult.shouldRedirect) {
    AuthService.redirectToLogin();
    return {
      settings: defaultSettings as Partial<LayoutSettings>,
      currentUser: undefined,
      isAuthenticated: false,
    };
  }

  // 定义fetchUserInfo函数供其他地方使用
  const fetchUserInfo = async (): Promise<
    import('@/types').CurrentUser | undefined
  > => {
    const result = await AuthService.authenticate();
    return result.user || undefined;
  };

  return {
    fetchUserInfo,
    currentUser: authResult.user || undefined,
    isAuthenticated: authResult.isAuthenticated,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  return {
    actionsRender: () => [
      // <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    avatarProps: {
      src: initialState?.currentUser?.icon,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name
        ? `${initialState.currentUser.name}`
        : initialState?.currentUser?.name || '访客',
    },
    footerRender: () => <Footer />,
    onPageChange: (_location) => {
      // 如果没有登录，重定向到外部登录页面
      if (!initialState?.isAuthenticated) {
        AuthService.redirectToLogin();
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    menuHeaderRender: undefined,
    // 移动端优化
    token: {
      header: {
        heightLayoutHeader: 48,
      },
      pageContainer: {
        paddingBlockPageContainerContent: 12,
        paddingInlinePageContainerContent: 12,
      },
    },
    childrenRender: (children) => {
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    title: false, // 隐藏layout标题
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // 在开发环境下不设置baseURL，让请求使用本地mock数据
  baseURL: undefined,
  ...errorConfig,
};
